import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { KnowledgeGraphResponse } from '../interfaces/visualization.interface';
import { InferencesApiResponse } from '../components/inferences/inferences.types';
import {
  ClustersResult,
  ClusterizeJobStatus,
  ClusterDocumentChunksResult,
  InferenceJobStatus
} from '../components/clusters/clusters.types';
import { CONTROLLER_CONSTANTS } from '../../../shared/constants/controller.constants';
import { ENDPOINT_CONSTANTS } from '../../../shared/constants/endpoint.constants';
import { BaseHttpService } from '../../../shared/services/base-http.service';
import { TenantService } from '../../../shared/services/tenant.service';
import { SILENCE_ERROR_TOAST } from '../../../shared/constants/http-context.tokens';

const CLUSTERIZE_POLL_INTERVAL_MS = 4000;
const INFERENCE_POLL_INTERVAL_MS = 4000;

@Injectable({
  providedIn: 'root'
})
export class VisualizationService {
  // Backed by a BehaviorSubject and polled from this root-provided service (not from
  // any component) so a run keeps progressing - and the latest status is available
  // to whoever asks - even if the user navigates away from the Clusters screen and
  // back. Tracking is deliberately NOT backed by localStorage: a job can run for days
  // on a large tenant, and the user may log out, close the browser, or come back on a
  // different device entirely before it finishes. None of that should matter - the job
  // itself runs server-side with no dependency on this browser tab, so
  // ensureTrackingForCurrentTenant() re-discovers it by asking the server "what's the
  // latest job for my tenant" (GET .../Clusterize/Latest) rather than relying on this
  // tab having remembered a job id.
  //
  // Tracking is scoped to the tenant the job was started under (trackedTenant). This
  // service is a single root-provided instance shared for the whole browser tab, so
  // without this a completed job's status/summary would otherwise leak into a
  // different tenant's Clusters screen after switching tenants/logging in as someone
  // else in the same tab - clusterizeStatus$ nulls itself out on a tenant mismatch.
  private clusterizeStatusSubject = new BehaviorSubject<ClusterizeJobStatus | null>(null);
  private trackedTenant: string | null = null;
  readonly clusterizeStatus$: Observable<ClusterizeJobStatus | null> = this.clusterizeStatusSubject.pipe(
    map(status => (status && this.trackedTenant === this.tenantService.currentTenantName() ? status : null))
  );
  private pollSub: Subscription | null = null;

  private inferenceJobStatusSubject = new BehaviorSubject<InferenceJobStatus | null>(null);
  private inferenceTrackedTenant: string | null = null;
  readonly inferenceJobStatus$: Observable<InferenceJobStatus | null> = this.inferenceJobStatusSubject.pipe(
    map(status =>
      status && this.inferenceTrackedTenant === this.tenantService.currentTenantName() ? status : null
    )
  );

  // inferenceJobStatus$ is a BehaviorSubject, so it replays its last value (e.g. a
  // "completed" job) to every new subscriber - including the Clusters screen's own
  // component, which is destroyed/recreated (via *ngIf) every time the user navigates
  // away and back. Reacting to "is this job_id new" with a field on that component was
  // wrong: the field resets on every remount, so the replayed "completed" status looked
  // new again on every single visit to Clusters, re-emitting inferencesGenerated and
  // forcing the user back to the Inferences screen. Tracking which job ids have already
  // been reacted to here instead - on this root-provided singleton - makes that state
  // survive the component's remounts for the lifetime of the tab.
  private handledInferenceJobIds = new Set<string>();

  hasHandledInferenceJob(jobId: string): boolean {
    return this.handledInferenceJobIds.has(jobId);
  }

  markInferenceJobHandled(jobId: string): void {
    this.handledInferenceJobIds.add(jobId);
  }
  private inferencePollSub: Subscription | null = null;

  constructor(private baseHttp: BaseHttpService, private tenantService: TenantService) {}

  getKnowledgeGraph(request: string[]): Observable<KnowledgeGraphResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_KNOWLEDGE_GRAPH}`;
    return this.baseHttp.post<KnowledgeGraphResponse>(endpoint, request);
  }
getDocuments(): Observable<KnowledgeGraphResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_ALL_DOCUMENTS}`;
    return this.baseHttp.get<KnowledgeGraphResponse>(endpoint);
  }

  // Documents/emails as they exist on the Neo4j knowledge graph - used for the
  // GenerateInferences selection picker so selections match graph node names exactly.
  getGraphDocuments(): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_GRAPH_DOCUMENTS}`;
    return this.baseHttp.get<any>(endpoint);
  }

  getInferences(): Observable<InferencesApiResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_INFERENCES}`;
    return this.baseHttp.get<InferencesApiResponse>(endpoint);
  }

  syncInferences(): Observable<InferencesApiResponse> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.SYNC_INFERENCES}`;
    return this.baseHttp.post<InferencesApiResponse>(endpoint, {});
  }

  // Starts Generate Inferences as a background job. Resolves almost immediately with a
  // queued/running status - subscribe to inferenceJobStatus$ for progress/completion.
  generateInferences(
    documentNames: string[],
    clusterId: number,
    clusterRunId?: string | null
  ): Observable<{ data?: InferenceJobStatus } & Partial<InferenceJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GENERATE_INFERENCES}`;
    return this.baseHttp.post<any>(endpoint, { documentNames, clusterId, clusterRunId }).pipe(
      tap((res: any) => {
        const job: InferenceJobStatus | undefined = res?.data ?? res;
        if (job?.job_id) {
          this.trackInferenceJob(job);
        }
      })
    );
  }

  getInferenceJobStatus(jobId: string): Observable<{ data?: InferenceJobStatus } & Partial<InferenceJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GENERATE_INFERENCES}/${jobId}`;
    return this.baseHttp.get<any>(endpoint);
  }

  getLatestInferenceJobStatus(): Observable<{ data?: InferenceJobStatus } & Partial<InferenceJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GENERATE_INFERENCES}/latest`;
    return this.baseHttp.get<any>(endpoint, undefined, new HttpContext().set(SILENCE_ERROR_TOAST, true));
  }

  // Starts the external clusterize run. The backend always runs it as a background
  // job, so this resolves almost immediately with a queued/running job status -
  // subscribe to clusterizeStatus$ (not this call's response) to observe progress
  // and the eventual result.
  startClusterize(minClusterSize?: number): Observable<{ data?: ClusterizeJobStatus } & Partial<ClusterizeJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.CLUSTERIZE}`;
    const body = minClusterSize ? { minClusterSize } : {};
    return this.baseHttp.post<any>(endpoint, body).pipe(
      tap((res: any) => {
        const job: ClusterizeJobStatus | undefined = res?.data ?? res;
        if (job?.job_id) {
          this.trackJob(job);
        }
      })
    );
  }

  getClusterizeStatus(jobId: string): Observable<{ data?: ClusterizeJobStatus } & Partial<ClusterizeJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.CLUSTERIZE}/${jobId}`;
    return this.baseHttp.get<any>(endpoint);
  }

  // Most recent clusterize job for whichever tenant the backend resolves for the current
  // request - no job id needed. 404s (this tenant has never run one) surface as the
  // observable erroring, same as any other non-2xx; callers treat that as "nothing to
  // track" rather than a real failure. SILENCE_ERROR_TOAST stops AuthInterceptor from
  // popping its app-wide error toast for that 404 - "no job yet" is a normal, silent
  // state (a fresh tenant hits it on literally every visit to Clusters until their
  // first run), not something worth alarming the user about.
  getLatestClusterizeStatus(): Observable<{ data?: ClusterizeJobStatus } & Partial<ClusterizeJobStatus>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.CLUSTERIZE}/latest`;
    return this.baseHttp.get<any>(endpoint, undefined, new HttpContext().set(SILENCE_ERROR_TOAST, true));
  }

  private trackJob(status: ClusterizeJobStatus): void {
    this.stopPolling();
    this.trackedTenant = this.tenantService.currentTenantName();
    this.clusterizeStatusSubject.next(status);

    if (status.status === 'completed' || status.status === 'failed') {
      return;
    }

    this.pollSub = interval(CLUSTERIZE_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.getClusterizeStatus(status.job_id).pipe(
            // A poll failing - a dropped connection, a token that's momentarily stale
            // right after logging back in, anything transient - must never be allowed to
            // kill this subscription. The job keeps running server-side regardless; a
            // failed tick just yields null here so the next tick tries again instead of
            // polling silently stopping forever (which previously looked, from the
            // user's side, exactly like the job itself had gotten stuck).
            catchError(() => of(null))
          )
        )
      )
      .subscribe((res: any) => {
        const latest: ClusterizeJobStatus | undefined = res?.data ?? res;
        if (!latest) {
          return;
        }
        this.clusterizeStatusSubject.next(latest);
        if (latest.status === 'completed' || latest.status === 'failed') {
          this.stopPolling();
        }
      });
  }

  // Call when a screen that displays clusterizeStatus$ mounts. If we're not already
  // tracking a job for the tenant currently in the URL, asks the server for that
  // tenant's latest job and resumes polling it if still in progress (or just shows its
  // final result if it already finished). This is a live server lookup, not a cache of
  // anything this browser remembered, so it works identically whether the user was away
  // for ten seconds or logged out on a different machine for three days while a
  // million-document run finished without anyone watching. A no-op if we're already
  // tracking this tenant.
  ensureTrackingForCurrentTenant(): void {
    if (this.trackedTenant === this.tenantService.currentTenantName()) {
      return;
    }

    this.getLatestClusterizeStatus()
      .pipe(catchError(() => of(null)))
      .subscribe((res: any) => {
        const status: ClusterizeJobStatus | undefined = res?.data ?? res;
        if (status) {
          this.trackJob(status);
        }
        // No job ever run for this tenant (404) or a transient lookup failure - leave
        // trackedTenant/the subject as they are; clusterizeStatus$ already nulls itself
        // out for a tenant with nothing tracked (see its own comment above).
      });
  }

  ensureInferenceTrackingForCurrentTenant(): void {
    if (this.inferenceTrackedTenant === this.tenantService.currentTenantName()) {
      return;
    }

    this.getLatestInferenceJobStatus()
      .pipe(catchError(() => of(null)))
      .subscribe((res: any) => {
        const status: InferenceJobStatus | undefined = res?.data ?? res;
        if (status && (status.status === 'queued' || status.status === 'running')) {
          this.trackInferenceJob(status);
        } else if (status) {
          this.inferenceTrackedTenant = this.tenantService.currentTenantName();
          this.inferenceJobStatusSubject.next(status);
        }
      });
  }

  private trackInferenceJob(status: InferenceJobStatus): void {
    this.stopInferencePolling();
    this.inferenceTrackedTenant = this.tenantService.currentTenantName();
    this.inferenceJobStatusSubject.next(status);

    if (status.status === 'completed' || status.status === 'failed') {
      return;
    }

    this.inferencePollSub = interval(INFERENCE_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.getInferenceJobStatus(status.job_id).pipe(catchError(() => of(null)))
        )
      )
      .subscribe((res: any) => {
        const latest: InferenceJobStatus | undefined = res?.data ?? res;
        if (!latest) {
          return;
        }
        this.inferenceJobStatusSubject.next(latest);
        if (latest.status === 'completed' || latest.status === 'failed') {
          this.stopInferencePolling();
        }
      });
  }

  private stopPolling(): void {
    if (this.pollSub && !this.pollSub.closed) {
      this.pollSub.unsubscribe();
    }
    this.pollSub = null;
  }

  private stopInferencePolling(): void {
    if (this.inferencePollSub && !this.inferencePollSub.closed) {
      this.inferencePollSub.unsubscribe();
    }
    this.inferencePollSub = null;
  }

  getClusters(page: number = 1, pageSize: number = 20): Observable<{ data?: ClustersResult } & Partial<ClustersResult>> {
    const endpoint = `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_CLUSTERS}`;
    return this.baseHttp.get<any>(endpoint, { page, pageSize });
  }

  getClusterDocumentChunks(
    clusterId: number,
    documentName: string
  ): Observable<{ data?: ClusterDocumentChunksResult } & Partial<ClusterDocumentChunksResult>> {
    const endpoint =
      `/${CONTROLLER_CONSTANTS.DOCUMENT}/${ENDPOINT_CONSTANTS.GET_CLUSTERS}` +
      `/${clusterId}/${ENDPOINT_CONSTANTS.GET_CLUSTER_CHUNKS}`;
    return this.baseHttp.get<any>(endpoint, { documentName });
  }
}
